<script setup>
import { computed } from 'vue'
import { Head } from '@inertiajs/vue3'
import {
  CreditCard, Receipt, History, FileText, CalendarClock, MessageCircle,
} from 'lucide-vue-next'
import PageHeader from '../Components/PageHeader.vue'
import Card from '../Components/Card.vue'
import CtaButton from '../Components/CtaButton.vue'
import InstallmentList from '../Components/InstallmentList.vue'
import HistoryFeed from '../Components/HistoryFeed.vue'
import DownloadLink from '../Components/DownloadLink.vue'
import { brl } from '../lib/format'

const props = defineProps({
  aircraft: { type: Object, required: true },
  contract: { type: Object, required: true },
  installments: { type: Array, required: true },
  receipts: { type: Array, required: true },
  history: { type: Array, required: true },
})

const settledPercent = computed(() =>
  Math.round((props.contract.paid / props.contract.total) * 100),
)

const totals = computed(() => [
  { label: 'Total do serviço', value: brl(props.contract.total), tone: 'text-up-navy' },
  { label: 'Pago',             value: brl(props.contract.paid), tone: 'text-up-green' },
  { label: 'Em aberto',        value: brl(props.contract.outstanding), tone: 'text-up-orange' },
])
</script>

<template>
  <Head title="Pagamentos" />

  <div class="space-y-6">
    <PageHeader title="PAGAMENTOS" :aircraft="aircraft" />

    <div class="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <div class="space-y-6 xl:col-span-2">
        <Card title="Resumo financeiro" :icon="CreditCard">
          <dl class="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div v-for="total in totals" :key="total.label" class="rounded-lg bg-up-bg px-4 py-3">
              <dt class="text-sm text-slate-500">{{ total.label }}</dt>
              <dd class="mt-0.5 text-xl font-bold" :class="total.tone">{{ total.value }}</dd>
            </div>
          </dl>

          <div class="mt-6">
            <p class="flex items-baseline justify-between text-sm">
              <span class="text-slate-500">Quitação do contrato</span>
              <span class="font-semibold text-up-navy">{{ settledPercent }}%</span>
            </p>
            <div
              class="mt-2 h-2.5 overflow-hidden rounded-full bg-slate-200"
              role="progressbar"
              :aria-valuenow="settledPercent"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-label="Percentual do contrato já quitado"
            >
              <div class="h-full rounded-full bg-up-green transition-[width] duration-500" :style="{ width: `${settledPercent}%` }" />
            </div>
          </div>
        </Card>

        <Card title="Parcelas" :icon="Receipt">
          <InstallmentList :installments="installments" />
        </Card>

        <Card title="Histórico" :icon="History">
          <HistoryFeed :entries="history" />
        </Card>
      </div>

      <div class="space-y-6">
        <Card title="Próximo vencimento" :icon="CalendarClock">
          <!-- Nulo quando não há parcela em aberto: tudo pago, ou cronograma
               ainda não lançado. -->
          <div v-if="contract.next_due" class="rounded-lg bg-up-bg px-4 py-4">
            <p class="text-sm text-slate-500">{{ contract.next_due.label }}</p>
            <p class="mt-1 text-2xl font-bold text-up-navy">{{ brl(contract.next_due.amount) }}</p>
            <p class="mt-1 text-sm text-up-amber">Vence em {{ contract.next_due.due_on }}</p>
          </div>
          <p v-else class="rounded-lg bg-up-bg px-4 py-4 text-sm text-slate-500">
            Nenhuma parcela em aberto no momento.
          </p>
          <p class="mt-4 text-sm leading-relaxed text-slate-500">
            As instruções de pagamento são enviadas pelo financeiro por e-mail antes de cada
            vencimento. Esta tela é apenas para acompanhamento.
          </p>
        </Card>

        <Card title="Comprovantes" :icon="FileText">
          <p v-if="!receipts.length" class="py-4 text-sm text-slate-500">
            Nenhum comprovante disponível ainda.
          </p>
          <ul v-else class="divide-y divide-slate-100">
            <li v-for="receipt in receipts" :key="receipt.name" class="flex items-center gap-3 py-3.5">
              <FileText class="h-5 w-5 shrink-0 text-up-navy" aria-hidden="true" />
              <div class="min-w-0">
                <p class="truncate text-sm font-medium text-up-navy">{{ receipt.name }}</p>
                <p class="text-xs text-slate-500">Emitido em {{ receipt.updated_at }}</p>
              </div>
              <DownloadLink :id="receipt.id" :name="receipt.name" :available="receipt.downloadable" />
            </li>
          </ul>
        </Card>

        <Card>
          <p class="text-sm leading-relaxed text-slate-600">
            Divergência em alguma parcela? Fale com o financeiro da UP Aviation.
          </p>
          <div class="pt-4">
            <CtaButton href="/suporte?assunto=financeiro" variant="outline" :icon="MessageCircle">
              Falar com o financeiro
            </CtaButton>
          </div>
        </Card>
      </div>
    </div>
  </div>
</template>
